import { useState, useMemo } from 'react';
import {
    Loader2, Lock, Unlock, RefreshCw, Download, AlertTriangle, ArrowRight,
} from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useToast } from '../components/ui/Toast';
import {
    useClassificationState, useYears, useSuppliers, useCustomers,
    COMPANIES, type Company,
} from '../hooks/useClassification';
import { classificationApi, type Tier, type SupplierRow, type CustomerRow } from '../services/classificationApi';

const COMPANY_LABELS: Record<Company, string> = {
    dch: 'Switzerland', dit: 'Italy', dsp: 'Spain', dtr: 'Türkiye',
};

function tierBadge(tier: Tier | null): { color: string; bg: string } {
    if (tier === 'A') return { color: 'text-emerald-700', bg: 'bg-emerald-100' };
    if (tier === 'B') return { color: 'text-amber-700',   bg: 'bg-amber-100' };
    if (tier === 'C') return { color: 'text-slate-600',   bg: 'bg-slate-100' };
    return { color: 'text-slate-400', bg: 'bg-slate-50' };
}

function fmt(n: number | null | undefined): string {
    if (n == null) return '—';
    return new Intl.NumberFormat('de-CH', { maximumFractionDigits: 0 }).format(n);
}

// ============================================================================
// Page
// ============================================================================
export function SpcClassificationPage() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/spc-classification');
    const { toast } = useToast();
    const { year, setYear, company, setCompany, tab, setTab } = useClassificationState();
    const { years, loading: yearsLoading } = useYears();

    if (permLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
        );
    }
    if (!canView) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-gray-500">You do not have access to this page.</p>
            </div>
        );
    }

    const yearRow = years.find(y => y.ClassYear === year);
    const supplierLocked = !!yearRow?.SupplierLockedAt;
    const customerLocked = !!yearRow?.CustomerLockedAt;
    const phase = customerLocked ? 'Locked' : supplierLocked ? 'Customer-Edit' : 'Supplier-Edit';

    return (
        <div className="space-y-4 p-4">
            {/* Top bar */}
            <div className="flex flex-wrap items-center gap-3 bg-white border rounded-lg p-3">
                <h1 className="text-lg font-semibold text-slate-800">SPC Classification</h1>

                <div className="flex items-center gap-2">
                    <label className="text-sm text-slate-600">Year:</label>
                    <select
                        value={year}
                        onChange={e => setYear(Number(e.target.value))}
                        className="border rounded px-2 py-1 text-sm"
                        disabled={yearsLoading}
                    >
                        {years.length === 0 && <option value={year}>{year}</option>}
                        {years.map(y => (
                            <option key={y.ClassYear} value={y.ClassYear}>{y.ClassYear}</option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center gap-1 ml-2">
                    {COMPANIES.map(c => (
                        <button
                            key={c}
                            onClick={() => setCompany(c)}
                            className={`px-3 py-1 text-sm rounded ${
                                company === c
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                        >
                            {COMPANY_LABELS[c]}
                        </button>
                    ))}
                </div>

                <div className="ml-auto flex items-center gap-2">
                    <span className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                        customerLocked ? 'bg-slate-200 text-slate-700'
                            : supplierLocked ? 'bg-amber-100 text-amber-800'
                            : 'bg-blue-100 text-blue-800'
                    }`}>
                        {customerLocked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                        Phase: {phase}
                    </span>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b">
                <TabButton active={tab === 'suppliers'} onClick={() => setTab('suppliers')}>
                    1. Suppliers {supplierLocked ? '✓' : ''}
                </TabButton>
                <TabButton active={tab === 'customers'} onClick={() => setTab('customers')}>
                    2. Customers {customerLocked ? '✓' : !supplierLocked ? ' (locked until Suppliers done)' : ''}
                </TabButton>
            </div>

            {/* Body */}
            {tab === 'suppliers' && (
                <SuppliersTab
                    year={year} company={company} canEdit={canEdit}
                    supplierLocked={supplierLocked}
                    onChanged={() => { /* hook handles refresh */ }}
                    toast={toast}
                />
            )}
            {tab === 'customers' && (
                <CustomersTab
                    year={year} company={company} canEdit={canEdit}
                    supplierLocked={supplierLocked}
                    customerLocked={customerLocked}
                    toast={toast}
                />
            )}
        </div>
    );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
    return (
        <button
            onClick={onClick}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-600 hover:text-slate-800'
            }`}
        >
            {children}
        </button>
    );
}

// ============================================================================
// Suppliers Tab
// ============================================================================
function SuppliersTab({
    year, company, canEdit, supplierLocked, onChanged: _onChanged, toast,
}: {
    year: number; company: Company; canEdit: boolean; supplierLocked: boolean;
    onChanged: () => void;
    toast: (m: string, t?: 'success' | 'error' | 'info') => void;
}) {
    const { data, loading, error, refresh } = useSuppliers(year, company);
    const [busy, setBusy] = useState(false);

    const recompute = async () => {
        if (!confirm(`Recompute suppliers for ${company.toUpperCase()} ${year}? Existing manual scores will be preserved.`)) return;
        setBusy(true);
        try {
            await classificationApi.recomputeSuppliers(year, company);
            toast('Recompute done', 'success');
            refresh();
        } catch (e) {
            toast(`Recompute failed: ${(e as Error).message}`, 'error');
        } finally { setBusy(false); }
    };

    const lock = async () => {
        if (!confirm(`Lock supplier classification for ${year}? After lock, scores cannot be edited and the A-Supplier list is fixed for the customer phase.`)) return;
        setBusy(true);
        try {
            await classificationApi.lockSuppliers(year);
            toast('Suppliers locked', 'success');
            window.location.reload(); // reload year-row state
        } catch (e) {
            toast(`Lock failed: ${(e as Error).message}`, 'error');
        } finally { setBusy(false); }
    };

    const exportXlsx = () => {
        window.location.href = classificationApi.exportUrl(year, 'supplier');
    };

    if (loading) return <Loader />;
    if (error) return <ErrorState msg={error} />;

    const rows = data?.rows ?? [];
    const showCanEdit = !!(canEdit && data?.canEdit && !supplierLocked);

    const counts = { A: 0, B: 0, C: 0, missing: 0 };
    for (const r of rows) {
        if (r.FinalTier === 'A') counts.A++;
        else if (r.FinalTier === 'B') counts.B++;
        else if (r.FinalTier === 'C') counts.C++;
        else counts.missing++;
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between bg-white border rounded p-3">
                <div className="flex items-center gap-4 text-sm">
                    <span className="text-slate-500">Total: <strong>{rows.length}</strong></span>
                    <Pill color="emerald">A: {counts.A}</Pill>
                    <Pill color="amber">B: {counts.B}</Pill>
                    <Pill color="slate">C: {counts.C}</Pill>
                    {counts.missing > 0 && <Pill color="rose">Missing scores: {counts.missing}</Pill>}
                </div>
                <div className="flex items-center gap-2">
                    {showCanEdit && (
                        <button onClick={recompute} disabled={busy}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                            <RefreshCw className="h-4 w-4" /> Recompute
                        </button>
                    )}
                    {showCanEdit && rows.length > 0 && (
                        <button onClick={lock} disabled={busy}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50">
                            <Lock className="h-4 w-4" /> Lock Phase 1
                        </button>
                    )}
                    <button onClick={exportXlsx}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200">
                        <Download className="h-4 w-4" /> Export
                    </button>
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="bg-white border rounded p-8 text-center text-slate-500">
                    No supplier rows yet. Click <strong>Recompute</strong> to load data from sales+budget.
                </div>
            ) : (
                <SupplierTable rows={rows} year={year} company={company} canEdit={showCanEdit} onSaved={refresh} toast={toast} />
            )}
        </div>
    );
}

function SupplierTable({
    rows, year, company, canEdit, onSaved, toast,
}: {
    rows: SupplierRow[]; year: number; company: Company; canEdit: boolean; onSaved: () => void;
    toast: (m: string, t?: 'success' | 'error' | 'info') => void;
}) {
    return (
        <div className="bg-white border rounded overflow-x-auto">
            <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-700 border-b">
                    <tr>
                        <th className="text-left px-3 py-2">Supplier</th>
                        <th className="text-right px-3 py-2">Finance GP</th>
                        <th className="text-center px-3 py-2">Fin Pts</th>
                        <th className="text-center px-3 py-2" title="Are we Important for them?">Imp</th>
                        <th className="text-center px-3 py-2" title="Family Owned">FamO</th>
                        <th className="text-center px-3 py-2" title="Local Sales Force">LocSF</th>
                        <th className="text-center px-3 py-2" title="More D countries">MoreD</th>
                        <th className="text-center px-3 py-2" title="Brand">Brand</th>
                        <th className="text-right px-3 py-2">Weighted</th>
                        <th className="text-center px-3 py-2">Tier</th>
                        <th className="text-center px-3 py-2">Δ PY</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <SupplierRowEditor
                            key={r.SupplierDimID} row={r} year={year} company={company}
                            canEdit={canEdit} onSaved={onSaved} toast={toast}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SupplierRowEditor({
    row, year, company, canEdit, onSaved, toast,
}: {
    row: SupplierRow; year: number; company: Company; canEdit: boolean; onSaved: () => void;
    toast: (m: string, t?: 'success' | 'error' | 'info') => void;
}) {
    const [draft, setDraft] = useState({
        scoreImportant: row.ScoreImportant,
        scoreFamilyOwned: row.ScoreFamilyOwned,
        scoreLocalSF: row.ScoreLocalSF,
        scoreMoreCountries: row.ScoreMoreCountries,
        scoreBrand: row.ScoreBrand,
        manualOverrideTier: row.ManualOverrideTier,
    });

    const save = async (next: Partial<typeof draft>) => {
        const patch = { ...draft, ...next };
        setDraft(patch);
        try {
            await classificationApi.updateSupplier(year, company, row.SupplierDimID, patch);
            toast(`${row.SupplierName} saved`, 'success');
            onSaved();
        } catch (e) {
            toast(`Save failed: ${(e as Error).message}`, 'error');
        }
    };

    const tierStyle = tierBadge(row.FinalTier);
    const diff = row.PreviousYearTier && row.PreviousYearTier !== row.FinalTier;

    return (
        <tr className="border-b last:border-b-0 hover:bg-slate-50">
            <td className="px-3 py-2">
                <div className="font-medium text-slate-800">{row.SupplierName}</div>
                <div className="text-xs text-slate-500">{row.SupplierDimID}</div>
            </td>
            <td className="px-3 py-2 text-right tabular-nums">{fmt(row.FinanceGpYear)}</td>
            <td className="px-3 py-2 text-center font-medium">{row.FinancePoints}</td>
            {(['scoreImportant','scoreFamilyOwned','scoreLocalSF','scoreMoreCountries','scoreBrand'] as const).map(k => (
                <td key={k} className="px-2 py-1 text-center">
                    <ScoreInput
                        value={draft[k]}
                        disabled={!canEdit}
                        onChange={v => save({ [k]: v } as Partial<typeof draft>)}
                    />
                </td>
            ))}
            <td className="px-3 py-2 text-right tabular-nums">{row.WeightedResult ?? '—'}</td>
            <td className="px-3 py-2 text-center">
                <TierSelect
                    computed={row.ComputedTier}
                    override={draft.manualOverrideTier}
                    disabled={!canEdit}
                    onChange={v => save({ manualOverrideTier: v })}
                />
                <span className={`ml-1 inline-block px-1.5 rounded text-xs ${tierStyle.bg} ${tierStyle.color}`}>
                    {row.FinalTier ?? '–'}
                </span>
            </td>
            <td className="px-3 py-2 text-center">
                {diff ? (
                    <span className="text-xs text-slate-600">
                        {row.PreviousYearTier} <ArrowRight className="inline h-3 w-3" /> {row.FinalTier}
                    </span>
                ) : <span className="text-slate-300">—</span>}
            </td>
        </tr>
    );
}

function ScoreInput({
    value, disabled, onChange,
}: { value: number | null; disabled: boolean; onChange: (v: number | null) => void }) {
    return (
        <select
            value={value ?? ''}
            disabled={disabled}
            onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
            className="w-12 border rounded text-center disabled:bg-slate-50 disabled:text-slate-400"
        >
            <option value="">–</option>
            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
    );
}

function TierSelect({
    computed, override, disabled, onChange,
}: {
    computed: Tier | null; override: Tier | null;
    disabled: boolean; onChange: (v: Tier | null) => void;
}) {
    return (
        <select
            value={override ?? ''}
            disabled={disabled}
            onChange={e => onChange(e.target.value === '' ? null : (e.target.value as Tier))}
            className="border rounded px-1 text-xs disabled:bg-slate-50"
            title={`Computed: ${computed ?? '–'}; manual override here`}
        >
            <option value="">auto</option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
        </select>
    );
}

// ============================================================================
// Customers Tab
// ============================================================================
function CustomersTab({
    year, company, canEdit, supplierLocked, customerLocked, toast,
}: {
    year: number; company: Company; canEdit: boolean;
    supplierLocked: boolean; customerLocked: boolean;
    toast: (m: string, t?: 'success' | 'error' | 'info') => void;
}) {
    const [tier, setTier] = useState<Tier | ''>('');
    const [onlyChanged, setOnlyChanged] = useState(false);
    const [onlyOverrides, setOnlyOverrides] = useState(false);
    const [search, setSearch] = useState('');

    const opts = useMemo(() => ({
        tier: tier || undefined,
        onlyChanged: onlyChanged || undefined,
        onlyOverrides: onlyOverrides || undefined,
        search: search || undefined,
    }), [tier, onlyChanged, onlyOverrides, search]);

    const { data, loading, error, refresh } = useCustomers(year, company, opts);
    const [busy, setBusy] = useState(false);

    if (!supplierLocked) {
        return (
            <div className="bg-amber-50 border border-amber-200 rounded p-6 text-center text-amber-900">
                <AlertTriangle className="h-6 w-6 mx-auto mb-2" />
                <strong>Phase 1 (Suppliers) is not yet locked.</strong>
                <p className="text-sm mt-1">Customer classification depends on the A-Supplier list. Lock Phase 1 first.</p>
            </div>
        );
    }

    const recompute = async () => {
        if (!confirm(`Recompute customers for ${company.toUpperCase()} ${year}? Existing manual overrides will be preserved.`)) return;
        setBusy(true);
        try {
            await classificationApi.recomputeCustomers(year, company);
            toast('Recompute done', 'success');
            refresh();
        } catch (e) {
            toast(`Recompute failed: ${(e as Error).message}`, 'error');
        } finally { setBusy(false); }
    };

    const lock = async () => {
        if (!confirm(`Lock customer classification for ${year}? FinalTier will be written back to etl.Customer.Classification.`)) return;
        setBusy(true);
        try {
            const r = await classificationApi.lockCustomers(year);
            toast(`Customers locked. ${(r as { etlCustomersUpdated?: number }).etlCustomersUpdated ?? 0} customers updated in ERP.`, 'success');
            window.location.reload();
        } catch (e) {
            toast(`Lock failed: ${(e as Error).message}`, 'error');
        } finally { setBusy(false); }
    };

    if (loading) return <Loader />;
    if (error) return <ErrorState msg={error} />;

    const rows = data?.rows ?? [];
    const showCanEdit = !!(canEdit && data?.canEdit && !customerLocked);

    const counts = { A: 0, B: 0, C: 0 };
    for (const r of rows) {
        if (r.FinalTier === 'A') counts.A++;
        else if (r.FinalTier === 'B') counts.B++;
        else if (r.FinalTier === 'C') counts.C++;
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between bg-white border rounded p-3">
                <div className="flex items-center gap-3 text-sm flex-wrap">
                    <span className="text-slate-500">Total: <strong>{rows.length}</strong></span>
                    <Pill color="emerald">A: {counts.A}</Pill>
                    <Pill color="amber">B: {counts.B}</Pill>
                    <Pill color="slate">C: {counts.C}</Pill>
                    {data?.scope === 'sales-manager' && data.appliedSalesManager && (
                        <span className="text-xs text-blue-700">SM: {data.appliedSalesManager}</span>
                    )}
                    <select value={tier} onChange={e => setTier(e.target.value as Tier | '')}
                        className="border rounded px-2 py-1 text-xs">
                        <option value="">All tiers</option>
                        <option value="A">Only A</option>
                        <option value="B">Only B</option>
                        <option value="C">Only C</option>
                    </select>
                    <label className="flex items-center gap-1 text-xs">
                        <input type="checkbox" checked={onlyChanged} onChange={e => setOnlyChanged(e.target.checked)} />
                        Only changed vs PY
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                        <input type="checkbox" checked={onlyOverrides} onChange={e => setOnlyOverrides(e.target.checked)} />
                        Only overrides
                    </label>
                    <input
                        type="text" placeholder="Search…" value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="border rounded px-2 py-1 text-xs"
                    />
                </div>
                <div className="flex items-center gap-2">
                    {showCanEdit && (
                        <button onClick={recompute} disabled={busy}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                            <RefreshCw className="h-4 w-4" /> Recompute
                        </button>
                    )}
                    {showCanEdit && rows.length > 0 && (
                        <button onClick={lock} disabled={busy}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50">
                            <Lock className="h-4 w-4" /> Lock Phase 2
                        </button>
                    )}
                    <button onClick={() => { window.location.href = classificationApi.exportUrl(year, 'customer'); }}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200">
                        <Download className="h-4 w-4" /> Export
                    </button>
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="bg-white border rounded p-8 text-center text-slate-500">
                    No customer rows yet. Click <strong>Recompute</strong> to compute scores.
                </div>
            ) : (
                <CustomerTable rows={rows} year={year} company={company} canEdit={showCanEdit} onSaved={refresh} toast={toast} />
            )}
        </div>
    );
}

function CustomerTable({
    rows, year, company, canEdit, onSaved, toast,
}: {
    rows: CustomerRow[]; year: number; company: Company; canEdit: boolean; onSaved: () => void;
    toast: (m: string, t?: 'success' | 'error' | 'info') => void;
}) {
    return (
        <div className="bg-white border rounded overflow-x-auto">
            <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-700 border-b">
                    <tr>
                        <th className="text-left px-3 py-2">Customer</th>
                        <th className="text-left px-3 py-2">Sales Manager</th>
                        <th className="text-right px-3 py-2">Total GP</th>
                        <th className="text-center px-3 py-2" title="GP rank 1..5">GP</th>
                        <th className="text-center px-3 py-2" title="A-Supplier rank 1..5">ASup</th>
                        <th className="text-center px-3 py-2" title="Project points 0..2">Proj</th>
                        <th className="text-center px-3 py-2" title="Payment delay points 0/1">PD</th>
                        <th className="text-right px-3 py-2">Score</th>
                        <th className="text-center px-3 py-2">Tier</th>
                        <th className="text-center px-3 py-2">Δ PY</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <CustomerRowEditor
                            key={`${r.CompanyCode}-${r.CustomerID}`} row={r} year={year} company={company}
                            canEdit={canEdit} onSaved={onSaved} toast={toast}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CustomerRowEditor({
    row, year, company, canEdit, onSaved, toast,
}: {
    row: CustomerRow; year: number; company: Company; canEdit: boolean; onSaved: () => void;
    toast: (m: string, t?: 'success' | 'error' | 'info') => void;
}) {
    const [override, setOverride] = useState<Tier | null>(row.ManualOverrideTier);
    const tierStyle = tierBadge(row.FinalTier);
    const diff = row.PreviousYearTier && row.PreviousYearTier !== row.FinalTier;

    const save = async (v: Tier | null) => {
        setOverride(v);
        try {
            await classificationApi.updateCustomer(year, company, row.CustomerID, { manualOverrideTier: v });
            toast(`${row.CustomerName} saved`, 'success');
            onSaved();
        } catch (e) {
            toast(`Save failed: ${(e as Error).message}`, 'error');
            setOverride(row.ManualOverrideTier); // rollback
        }
    };

    return (
        <tr className="border-b last:border-b-0 hover:bg-slate-50">
            <td className="px-3 py-2">
                <div className="font-medium text-slate-800">{row.CustomerName}</div>
                <div className="text-xs text-slate-500">{row.CustomerID}</div>
            </td>
            <td className="px-3 py-2 text-slate-600 text-xs">{row.SalesManagerSnapshot}</td>
            <td className="px-3 py-2 text-right tabular-nums">{fmt(row.TotalGp)}</td>
            <td className="px-3 py-2 text-center">{row.GpRanking}</td>
            <td className="px-3 py-2 text-center">{row.ASupplierRanking}</td>
            <td className="px-3 py-2 text-center">
                {row.ProjectPoints}
                {row.ProjectExpectedGp > 0 && (
                    <span className="ml-1 text-xs text-slate-400">({fmt(row.ProjectExpectedGp)})</span>
                )}
            </td>
            <td className="px-3 py-2 text-center">
                {row.PaymentDelayPoints}
                {row.PaymentAvgDelay != null && (
                    <span className="ml-1 text-xs text-slate-400">({Number(row.PaymentAvgDelay).toFixed(1)}d)</span>
                )}
            </td>
            <td className="px-3 py-2 text-right tabular-nums font-medium">{row.ComputedScore}</td>
            <td className="px-3 py-2 text-center">
                <TierSelect
                    computed={row.ComputedTier} override={override}
                    disabled={!canEdit} onChange={save}
                />
                <span className={`ml-1 inline-block px-1.5 rounded text-xs ${tierStyle.bg} ${tierStyle.color}`}>
                    {row.FinalTier ?? '–'}
                </span>
            </td>
            <td className="px-3 py-2 text-center">
                {diff ? (
                    <span className="text-xs text-slate-600">
                        {row.PreviousYearTier} <ArrowRight className="inline h-3 w-3" /> {row.FinalTier}
                    </span>
                ) : <span className="text-slate-300">—</span>}
            </td>
        </tr>
    );
}

// ============================================================================
// Small UI helpers
// ============================================================================
function Loader() {
    return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div>;
}

function ErrorState({ msg }: { msg: string }) {
    return <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded p-4">{msg}</div>;
}

function Pill({ color, children }: { color: 'emerald' | 'amber' | 'slate' | 'rose' | 'blue'; children: React.ReactNode }) {
    const map = {
        emerald: 'bg-emerald-100 text-emerald-800',
        amber: 'bg-amber-100 text-amber-800',
        slate: 'bg-slate-100 text-slate-700',
        rose: 'bg-rose-100 text-rose-800',
        blue: 'bg-blue-100 text-blue-800',
    };
    return <span className={`px-2 py-0.5 rounded text-xs ${map[color]}`}>{children}</span>;
}
